<template>
  <LayoutOverlay class="menu-calendar" @closeOverlay="emits('calendarClose')">
    <div class="menu-calendar-settings">
      <div class="menu-calendar-settings__top-row">
        <div class="menu-calendar-settings__top-row__calendar">
          <CalendarView
            :startDate="startDate"
            :endDate="endDate"
            @dateChanged="dateChanged"
            @dragChanged="dragChanged"
          />
          <div class="menu-calendar-settings__top-row__checkbox">
            <CheckBoxStandard
              v-if="filters && 'onlyBusinessDays' in filters.current"
              :checked="onlyBusinessDays"
              :title="t('general__text__business-days-only')"
              :description="t('general__text__mon-fri')"
              @valueChanged="() => (onlyBusinessDays = !onlyBusinessDays)"
            />
            <CheckBoxStandard
              v-if="filters && 'onlyBusinessHours' in filters.current"
              :checked="onlyBusinessHours"
              :title="t('general__text__work-hours-only')"
              :description="t('general__text__work-hours')"
              @valueChanged="() => (onlyBusinessHours = !onlyBusinessHours)"
            />
          </div>
        </div>
        <div class="menu-calendar-settings__top-row__quick-select">
          <div class="menu-calendar-settings__top-row__quick-select__header">
            <span class="menu-calendar-settings__top-row__quick-select__header__text">
              <InfoLabel :title="t('general__text__quickselect')" :info="t('tooltip__calendar')" />
            </span>
          </div>
          <div class="menu-calendar-settings__top-row__quick-select__buttons">
            <ButtonCalendarQuickSelect
              v-for="option in options"
              :key="option.name"
              :selected="option.name == selectedOption"
              v-bind="option"
              @onClicked="option.onClick"
            />
          </div>
        </div>
      </div>
      <div class="calendar-footer">
        <div class="input-container">
          <span class="input-container__help-message">{{
            t('general__text__calendar_input_msg')
          }}</span>
          <div class="row">
            <InputCalendarDate
              :inputValue="input.start.value"
              :error="input.start.error"
              @inputValueChanged="inputStartValueChanged"
            />
            <span><IconSmallArrowRight /></span>
            <InputCalendarDate
              :inputValue="input.end.value"
              :error="input.end.error"
              @inputValueChanged="inputEndValueChanged"
            />
          </div>
        </div>
        <div class="button-container">
          <div class="row">
            <ButtonOutline
              class="button"
              :buttonText="t('general__text__cancel')"
              @onClicked="() => $emit('calendarClose')"
            />
            <ButtonPrimary
              class="button"
              :buttonText="t('general__text__done')"
              :disabled="!hasChanges || !isFormValid"
              @onClicked="saveDates"
            />
          </div>
        </div>
      </div>
    </div>
  </LayoutOverlay>
</template>

<script lang="ts" setup>
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import CalendarView from '@/design-system/molecules/CalendarView.vue'
import ButtonCalendarQuickSelect from '@/design-system/atoms/buttons/ButtonCalendarQuickSelect.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import InputCalendarDate from '@/design-system/atoms/InputCalendarDate.vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import IconSmallArrowRight from '@/design-system/assets/icons/IconSmallArrowRight.vue'
import { computed, onMounted, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { format } from 'date-fns'
import { LOCALES } from '@/utils/locales'
import { useCurrentFeatureFilters } from '@/composables/useFilters'
import { QuickSelect, QUICK_SELECTS, QuickSelectType } from '../../types'
import { quickSelectToLastQuarter, quickSelectToTimeBefore } from '@/utils/calendar-utils'
import { useTimeSelection } from '@/composables/useTimeRange'

interface EMITS {
  (e: 'calendarClose'): void
  (e: 'calendarSubmit'): void
}

type QuickSelectWithIcon = {
  quickSelect: QuickSelect
  icon: string
}

type Option = {
  name: string
  title: string
  subtitle: string
  src: string
  onClick: () => void
}

const { t, locale } = useI18n()

const filters = useCurrentFeatureFilters()
const timeSelection = useTimeSelection()

const quarterIcons: Record<number, string> = {
  1: 'Icon-Q1_x32',
  2: 'Icon-Q2_x32',
  3: 'Icon-Q3_x32',
  4: 'Icon-Q4_x32'
}

const quickSelectsWithIcon: QuickSelectWithIcon[] = [
  {
    quickSelect: QUICK_SELECTS[0],
    icon: 'Icon-Last-1-Days_x32'
  },
  {
    quickSelect: QUICK_SELECTS[1],
    icon: 'Icon-Last-7-Days_x32'
  },
  {
    quickSelect: QUICK_SELECTS[2],
    icon: 'Icon-Last-30-Days_x32'
  },
  {
    quickSelect: QUICK_SELECTS[3],
    icon: quarterIcons[quickSelectToLastQuarter(new Date(), QUICK_SELECTS[3])]
  },
  {
    quickSelect: QUICK_SELECTS[4],
    icon: 'Icon-Last-12-Months_x32'
  },
  {
    quickSelect: QUICK_SELECTS[5],
    icon: 'Icon-Current-Year_x32'
  },
  {
    quickSelect: QUICK_SELECTS[6],
    icon: 'Icon-Previous-Year_x32'
  }
]

function dateFormatter(value: string) {
  return format(new Date(value), 'yyyy-MM-dd')
}

const quickSelect = ref<QuickSelect | undefined | null>()
const onlyBusinessDays = ref<boolean>(false)
const onlyBusinessHours = ref<boolean>(false)
const startDate = ref<string>()
const endDate = ref<string | undefined>()
const selectedOption = ref<string | undefined | null>()
const snapshot = ref<any | undefined>(undefined)

const options = computed<Option[]>(() =>
  quickSelectsWithIcon.map((item) => {
    const timeEntry = quickSelectToTimeBefore(new Date(), item.quickSelect, LOCALES[locale.value])
    return {
      name: item.quickSelect.name,
      title: item.quickSelect.title[locale.value],
      subtitle: timeEntry.formatted,
      src: item.icon,
      onClick: () => {
        startDate.value = dateFormatter(timeEntry.from)
        endDate.value = dateFormatter(timeEntry.to)
        selectedOption.value = item.quickSelect.name
      }
    }
  })
)

onMounted(() => {
  if (filters.value?.current && 'calendar' in filters.value.current) {
    const currentFilter = filters.value.current
    const currentCalendar = currentFilter.calendar
    onlyBusinessDays.value = 'onlyBusinessDays' in currentFilter && currentFilter.onlyBusinessDays
    onlyBusinessHours.value =
      'onlyBusinessHours' in currentFilter && currentFilter.onlyBusinessHours

    if (currentCalendar && currentCalendar.quickSelect) {
      const found = QUICK_SELECTS.find((item) => item.name === currentCalendar.quickSelect)
      if (found) {
        quickSelect.value = found
        selectedOption.value = currentCalendar.quickSelect
        const timeEntry = quickSelectToTimeBefore(
          new Date(),
          quickSelect.value,
          LOCALES[locale.value]
        )
        startDate.value = dateFormatter(timeEntry.from)
        endDate.value = dateFormatter(timeEntry.to)
      }
    } else if (currentCalendar && currentCalendar.from && currentCalendar.to) {
      startDate.value = dateFormatter(currentCalendar.from)
      endDate.value = dateFormatter(currentCalendar.to)
    }
    snapshot.value = {
      startDate: startDate.value,
      endDate: endDate.value,
      onlyBusinessDays: onlyBusinessDays.value,
      onlyBusinessHours: onlyBusinessHours.value
    }
  }
})

const hasChanges = computed(() => {
  if (!snapshot.value) {
    return false
  }
  return (
    snapshot.value.startDate !== startDate.value ||
    snapshot.value.endDate !== endDate.value ||
    snapshot.value.onlyBusinessDays !== onlyBusinessDays.value ||
    snapshot.value.onlyBusinessHours !== onlyBusinessHours.value
  )
})

const isFormValid = computed(() => {
  if (input.value.start.value && input.value.end.value) {
    const start = input.value.start.value
    const end = input.value.end.value
    if (isValidFormat(start) && isValidFormat(end) && !isValueLaterThanToday(end)) {
      return true
    }
  }
  return false
})

const input = ref<{
  start: {
    value?: string
    error?: string
  }
  end: {
    value?: string
    error?: string
  }
}>({
  start: { value: startDate.value },
  end: { value: endDate.value }
})

watchEffect(() => {
  input.value.start.value = startDate.value ? dateFormatter(startDate.value) : undefined
  input.value.end.value = endDate.value ? dateFormatter(endDate.value) : undefined
})

const emits = defineEmits<EMITS>()

function dateChanged(value: { start?: string; end?: string }) {
  input.value = { start: {}, end: {} }
  startDate.value = value.start ? dateFormatter(value.start) : undefined
  endDate.value = value.end ? dateFormatter(value.end) : undefined
}

function dragChanged() {
  quickSelect.value = null
  selectedOption.value = null
}

function inputStartValueChanged(value: string) {
  quickSelect.value = null
  selectedOption.value = null
  input.value.start.error = undefined
  if (isValidFormat(value)) {
    if (input.value.end.value && input.value.end.value < value) {
      input.value.start.value = value
      input.value.start.error = t('general__text__start_date_after_end_date')
    } else {
      input.value.start.value = value
      startDate.value = value
    }
  } else {
    input.value.start.value = value
    input.value.start.error = t('general__text__invalid_date')
  }
}

function inputEndValueChanged(value: string) {
  quickSelect.value = null
  selectedOption.value = null
  input.value.end.error = undefined
  if (isValidFormat(value)) {
    if (input.value.start.value && input.value.start.value > value) {
      input.value.end.value = value
      input.value.end.error = t('general__text__end_date_before_start_date')
    } else if (isValueLaterThanToday(value)) {
      input.value.end.value = value
      input.value.end.error = t('general__text__end_date_before_start_date')
    } else {
      input.value.end.value = value
      endDate.value = value
    }
  } else {
    input.value.end.value = value
    input.value.end.error = t('general__text__invalid_date')
  }
}

function saveDates() {
  if (startDate.value && endDate.value) {
    timeSelection.set('custom', [{ absolute: startDate.value }, { absolute: endDate.value }])
  }

  if (
    filters.value?.current &&
    'calendar' in filters.value.current &&
    filters.value.current.calendar
  ) {
    filters.value.current.calendar.quickSelect = selectedOption.value
      ? (selectedOption.value as QuickSelectType)
      : undefined
    filters.value.current.calendar.from = startDate.value
      ? dateFormatter(startDate.value)
      : undefined
    filters.value.current.calendar.to = endDate.value ? dateFormatter(endDate.value) : undefined
    filters.value.resetBucketSize()

    if ('onlyBusinessDays' in filters.value.current) {
      filters.value.$patch({
        current: {
          onlyBusinessDays: onlyBusinessDays.value
        }
      })
    }

    if ('onlyBusinessHours' in filters.value.current) {
      filters.value.$patch({
        current: {
          onlyBusinessHours: onlyBusinessHours.value
        }
      })
    }
    emits('calendarSubmit')
  }
}

function isValidFormat(value: string) {
  const pattern = '^(19|20)\\d\\d[-](0[1-9]|1[012])[-](0[1-9]|[12][0-9]|3[01])$'
  const regex: RegExp = new RegExp(pattern)
  return regex.test(value)
}

function isValueLaterThanToday(value: string) {
  return value > dateFormatter(new Date().toISOString())
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/mixins.scss';

.calendar-footer {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: variables.$color-greyscale-background;
  border-bottom-left-radius: variables.$border-radius-large;
  border-bottom-right-radius: variables.$border-radius-large;
  padding: variables.$padding-xlarge;
  .input-container {
    display: flex;
    flex-direction: column;
    &__help-message {
      @include font-mixins.desktop-regular-label;
      color: variables.$color-greyscale-content;
      padding-bottom: variables.$padding-medium;
    }
    .row {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: variables.$gap-large;
      span {
        transform: translateY(calc(-25%));
        padding: 0;
        display: block;
        color: black;
      }
    }
  }
  .button-container {
    display: flex;
    flex-direction: row;
    align-content: center;
    align-items: center;
    .row {
      display: flex;
      flex-direction: row;
      align-content: center;
      align-items: center;
      gap: variables.$gap-xlarge;
      .button {
        width: 120px;
      }
    }
  }
}
.menu-calendar {
  left: 0;
}
.menu-calendar-settings {
  background: variables.$color-pure-white;
  border: variables.$border-width-xsmall solid variables.$color-pure-white;
  border-radius: variables.$border-radius-large;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: grid;
  &__top-row {
    display: flex;
    flex-direction: row;
    &__calendar {
      display: flex;
      flex-direction: column;
      padding: 16px;
      gap: variables.$gap-xlarge;
      color: variables.$color-text-primary;
    }
    &__checkbox {
      display: flex;
      flex-direction: row;
      gap: 100px;
    }
    &__quick-select {
      display: flex;
      flex-direction: column;
      border-left: variables.$border-width-xsmall solid variables.$color-lines;
      padding: 22px;
      width: 346px;
      height: 462px;
      &__header {
        display: flex;
        justify-content: center;
        @include font-mixins.desktop-regular-paragraph;
        margin-bottom: 24px;
        gap: 1.5px;
        &__text {
          color: variables.$color-greyscale-content;
        }
      }
      &__buttons {
        display: flex;
        flex-direction: column;
        padding: 4px;
        margin-right: 12px;
        gap: 10px;
        width: 100%;
        overflow-y: auto;
        @include mixins.scrollbar-vertical;
        &__icon {
          color: variables.$color-greyscale-secondary;
        }
      }
    }
  }
}
</style>
